import type { AttributeRulesManager } from './use-rule-draft';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AttributeRuleDialog } from './AttributeRuleDialog';
import { describeRule } from './attribute-rules';
import { COPY } from './copy';

/** The words around the rules, when they are not a Server-side experiment's enrolment rules. */
export type AttributeRulesCopy = {
  label: string;
  help: string;
  empty: string;
  full: string;
  dialogHelp: readonly string[];
};

const EXPERIMENT_RULES_COPY: AttributeRulesCopy = {
  label: COPY.rules.label,
  help: COPY.rules.help,
  empty: COPY.rules.empty,
  full: COPY.rules.full,
  dialogHelp: COPY.rules.dialogHelp,
};

type AttributeRulesEditorProps = { manager: AttributeRulesManager; copy?: AttributeRulesCopy };

const SECONDARY_BUTTON_CLASS =
  'border-line text-foreground hover:bg-hover h-8 rounded-lg border bg-transparent px-3 text-meta';

/**
 * The attribute rules as chips, with an Add button behind a confirm dialog. Rules may change
 * while the experiment runs: the API republishes the SDK config on every change. A feature
 * flag's rings reuse it with their own copy, keeping the rules in the ring's draft.
 */
export const AttributeRulesEditor = ({ manager, copy = EXPERIMENT_RULES_COPY }: AttributeRulesEditorProps) => {
  const { rules, rulesFull, isSaving, openRuleDialog, removeRule } = manager;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="micro-label">{copy.label}</span>
          <p className="supporting m-0 mt-1">{copy.help}</p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={openRuleDialog}
          disabled={rulesFull || isSaving}
          title={rulesFull ? copy.full : undefined}
          className={SECONDARY_BUTTON_CLASS}
        >
          <Plus className="size-3.5" strokeWidth={1.5} />
          {COPY.rules.add}
        </Button>
      </div>

      {rules.length === 0 ? (
        <p className="supporting m-0">{copy.empty}</p>
      ) : (
        <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
          {rules.map((rule, idx) => {
            const label = describeRule(rule);
            return (
              <li
                key={`${idx}-${label}`}
                className="border-line bg-field text-foreground flex items-center gap-1.5 rounded-lg border py-1 pr-1 pl-2.5 font-mono text-micro"
              >
                <span className="break-all">{label}</span>
                <button
                  type="button"
                  onClick={() => removeRule(idx)}
                  disabled={isSaving}
                  aria-label={`${COPY.rules.remove}: ${label}`}
                  title={COPY.rules.remove}
                  className="text-dim hover:text-foreground hover:bg-hover rounded p-0.5"
                >
                  <X className="size-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {rulesFull && <p className="supporting m-0">{copy.full}</p>}

      <AttributeRuleDialog manager={manager} help={copy.dialogHelp} />
    </div>
  );
};
